/**
 * Texto escrito pelo time, com as menções realçadas e os links clicáveis.
 *
 * <p>Serve a descrição da tarefa e o corpo do comentário — os dois lugares onde alguém
 * cola um endereço. Antes o link ficava lá como texto morto: dava para ler e não dava
 * para abrir, o que obriga a selecionar, copiar e colar na barra do navegador.
 *
 * <h2>Por que não usar um renderizador de markdown</h2>
 *
 * <p>Porque o texto <b>não é markdown</b>, e tratá-lo como tal muda o que está escrito:
 * um `*` de bullet vira itálico, um `#` de issue vira título, e um trecho de código com
 * `_` some pela metade. Aqui só duas coisas viram algo — a menção e a URL —, e o resto
 * do texto sai exatamente como foi digitado.
 *
 * <h2>A regra de segurança</h2>
 *
 * <p>O texto <b>nunca</b> entra na página como HTML: o React escapa tudo que passa como
 * filho, e as únicas tags aqui são as que este componente cria. Não há
 * {@code dangerouslySetInnerHTML}, e por isso não há o que higienizar.
 *
 * <p>Só {@code http} e {@code https} viram link. {@code javascript:} num href executa no
 * clique — é a forma clássica de transformar "alguém colou um link" em "alguém rodou
 * código na sua sessão". Como o esquema é conferido antes de virar {@code <a>}, um
 * {@code javascript:alert(1)} colado num comentário continua sendo o texto que ele é.
 */
import type { Membro } from '../api/tipos';
import './textoRico.css';

/**
 * O que vira link e o que vira menção.
 *
 * <p>Um regex só, com dois grupos, porque a divisão precisa acontecer numa passada: com
 * dois {@code split} encadeados, o segundo quebraria dentro do que o primeiro já tinha
 * marcado como link — e um endereço com `@` (que é comum) viraria meia menção.
 *
 * <p>O fim da URL exclui pontuação final para o ponto de uma frase não entrar no
 * endereço: "veja em https://exemplo.com." não deve linkar o ponto.
 */
const PEDACOS = /(https?:\/\/[^\s<>()[\]{}"']+[^\s<>()[\]{}"'.,;:!?])|(@[\w.-]+)/g;

/** O texto que o link mostra, sem o ruído que não ajuda a reconhecer o destino. */
function rotuloDoLink(url: string): string {
  try {
    const u = new URL(url);
    const caminho = u.pathname === '/' ? '' : u.pathname;
    const curto = `${u.host}${caminho}`;
    // 48 é o que cabe numa linha do modal sem empurrar o resto.
    return curto.length > 48 ? `${curto.slice(0, 47)}…` : curto;
  } catch {
    return url;
  }
}

export function TextoRico({ texto, membros }: { texto: string; membros: Membro[] }) {
  const handles = new Set(membros.map((m) => m.handle.toLowerCase()));
  const pedacos = texto.split(PEDACOS);

  return (
    <>
      {pedacos.map((p, i) => {
        if (!p) return null;

        if (/^https?:\/\//i.test(p)) {
          return (
            <a
              key={i}
              className="txt-link"
              href={p}
              target="_blank"
              // noreferrer junto: sem ele, a página aberta recebe o endereço de onde
              // veio, e `noopener` sozinho não cobre navegadores antigos.
              rel="noopener noreferrer"
              title={p}
              // O clique não pode acionar o que está embaixo — o corpo do comentário
              // e o card inteiro costumam ter onClick próprio.
              onClick={(e) => e.stopPropagation()}
            >
              {rotuloDoLink(p)}
            </a>
          );
        }

        if (p.startsWith('@') && handles.has(p.slice(1).toLowerCase())) {
          return (
            <b key={i} className="cm-mencao">
              {p}
            </b>
          );
        }

        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
